/**
 * L'envoi d'un lot de fichiers vers un dépôt de reprise (ARTO-129), commun à
 * la console et à la page publique du lien de dépôt.
 *
 * Indépendant du navigateur : les octets (`tranche`) et les requêtes
 * (`TransportDepot`) sont fournis par l'appelant. Ce module ne fait que
 * l'ordonnancement : quatre morceaux en vol au plus, tous fichiers confondus ;
 * les morceaux déjà reçus sautés ; une coupure réessayée avec une attente
 * croissante ; un refus définitif arrête son fichier, pas les autres ; chaque
 * fichier achevé une seule fois, quand son dernier morceau est parti.
 */
import { ENVOIS_PARALLELES_DEPOT, nombreDeMorceaux, TAILLE_MORCEAU_DEPOT, tailleDuMorceau, } from './depot.js';
/** Une erreur du transport ; `definitive` quand réessayer ne changera rien (un refus). */
export class ErreurEnvoiDepot extends Error {
    definitive;
    raison;
    constructor(message, definitive, raison = null) {
        super(message);
        this.name = 'ErreurEnvoiDepot';
        this.definitive = definitive;
        this.raison = raison;
    }
}
/** Une URL signée vaut quinze minutes ; on la renouvelle au bout de dix. */
const DUREE_URL_MS = 10 * 60 * 1000;
/** URL demandées d'un coup, au plus. */
const LOT_SIGNATURES = 8;
/** `setTimeout`, que le socle partagé ne type pas (ni DOM, ni Node). */
const minuterie = globalThis;
const attendreParDefaut = (ms) => new Promise((resoudre) => {
    minuterie.setTimeout(resoudre, ms);
});
export async function envoyerFichiersDepot(fichiers, transport, options = {}) {
    const parallelisme = Math.max(1, options.parallelisme ?? ENVOIS_PARALLELES_DEPOT);
    const tentatives = Math.max(1, options.tentatives ?? 6);
    const attendre = options.attendre ?? attendreParDefaut;
    const etats = fichiers.map((source, index) => ({
        index,
        source,
        fichierId: null,
        mode: 'api',
        attente: [],
        enVol: 0,
        confirmes: 0,
        enCours: new Map(),
        urls: new Map(),
        etat: 'attente',
        erreur: null,
        raison: null,
    }));
    const suivi = (etat) => {
        let enVol = 0;
        for (const octets of etat.enCours.values())
            enVol += octets;
        return {
            chemin: etat.source.chemin,
            octets: etat.source.octets,
            envoyes: Math.min(etat.source.octets, etat.confirmes + enVol),
            etat: etat.etat,
            erreur: etat.erreur,
            raison: etat.raison,
        };
    };
    const signaler = (etat) => options.surSuivi?.(etat.index, suivi(etat));
    /** Réessaie ce qui peut l'être, avec une attente croissante. */
    async function avecReprises(geste) {
        for (let essai = 1;; essai += 1) {
            try {
                return await geste();
            }
            catch (erreur) {
                const definitive = erreur instanceof ErreurEnvoiDepot && erreur.definitive;
                if (definitive || essai >= tentatives || options.signal?.aborted === true)
                    throw erreur;
                await attendre(Math.min(30_000, 1000 * 2 ** (essai - 1)));
            }
        }
    }
    const echouer = (etat, erreur) => {
        if (etat.etat === 'erreur')
            return;
        etat.etat = 'erreur';
        etat.attente = [];
        etat.erreur = erreur instanceof Error ? erreur.message : String(erreur);
        etat.raison = erreur instanceof ErreurEnvoiDepot ? erreur.raison : null;
        signaler(etat);
    };
    async function ouvrir(etat) {
        etat.etat = 'envoi';
        signaler(etat);
        try {
            const ouverture = await avecReprises(() => transport.ouvrir(etat.source.chemin, etat.source.octets));
            etat.fichierId = ouverture.fichierId;
            etat.mode = ouverture.mode;
            const recus = new Set(ouverture.dejaRecus);
            const total = nombreDeMorceaux(etat.source.octets);
            for (let numero = 1; numero <= total; numero += 1) {
                if (recus.has(numero))
                    etat.confirmes += tailleDuMorceau(etat.source.octets, numero);
                else
                    etat.attente.push(numero);
            }
            signaler(etat);
            if (etat.attente.length === 0)
                await terminer(etat);
        }
        catch (erreur) {
            echouer(etat, erreur);
        }
    }
    async function terminer(etat) {
        const fichierId = etat.fichierId;
        if (fichierId === null)
            return;
        // Un fichier vide est reçu dès son ouverture : rien à achever.
        if (etat.source.octets > 0)
            await avecReprises(() => transport.terminer(fichierId));
        etat.etat = 'termine';
        signaler(etat);
    }
    async function url(etat, numero) {
        if (etat.mode !== 'direct' || etat.fichierId === null)
            return null;
        const connue = etat.urls.get(numero);
        if (connue !== undefined && Date.now() - connue.le < DUREE_URL_MS)
            return connue.url;
        const fichierId = etat.fichierId;
        const numeros = [numero, ...etat.attente.filter((n) => !etat.urls.has(n))].slice(0, LOT_SIGNATURES);
        // Pas de reprise ici : c'est l'envoi du morceau, tout entier, qui se réessaie.
        const signes = await transport.signer(fichierId, numeros);
        const le = Date.now();
        for (const signe of signes)
            etat.urls.set(signe.numero, { url: signe.url, le });
        return etat.urls.get(numero)?.url ?? null;
    }
    async function envoyerMorceau(etat, numero) {
        const fichierId = etat.fichierId;
        if (fichierId === null)
            return;
        const debut = (numero - 1) * TAILLE_MORCEAU_DEPOT;
        const fin = debut + tailleDuMorceau(etat.source.octets, numero);
        etat.enVol += 1;
        try {
            await avecReprises(async () => {
                etat.enCours.set(numero, 0);
                try {
                    const cible = await url(etat, numero);
                    await transport.envoyer({ fichierId, numero, url: cible }, etat.source.tranche(debut, fin), (octets) => {
                        etat.enCours.set(numero, octets);
                        signaler(etat);
                    });
                }
                catch (erreur) {
                    // Une URL refusée (périmée) se signe de nouveau à l'essai suivant,
                    // et les octets du morceau raté ne comptent plus comme partis.
                    etat.urls.delete(numero);
                    etat.enCours.delete(numero);
                    signaler(etat);
                    throw erreur;
                }
                finally {
                    etat.enCours.delete(numero);
                }
            });
            etat.confirmes += fin - debut;
            etat.urls.delete(numero);
            etat.enVol -= 1;
            signaler(etat);
            if (etat.etat === 'envoi' && etat.enVol === 0 && etat.attente.length === 0)
                await terminer(etat);
        }
        catch (erreur) {
            etat.enVol -= 1;
            echouer(etat, erreur);
        }
    }
    // Un travailleur sans tâche attend qu'une ouverture en cours en apporte :
    // un seul gros fichier se partage ainsi entre les quatre.
    let prochain = 0;
    let ouvertures = 0;
    let endormis = [];
    const reveiller = () => {
        const tous = endormis;
        endormis = [];
        for (const reveil of tous)
            reveil();
    };
    async function travailleur() {
        while (options.signal?.aborted !== true) {
            const avecMorceau = etats.find((etat) => etat.etat === 'envoi' && etat.attente.length > 0);
            const numero = avecMorceau?.attente.shift();
            if (avecMorceau !== undefined && numero !== undefined) {
                await envoyerMorceau(avecMorceau, numero);
                continue;
            }
            const suivant = etats[prochain];
            if (suivant !== undefined) {
                prochain += 1;
                ouvertures += 1;
                try {
                    await ouvrir(suivant);
                }
                finally {
                    ouvertures -= 1;
                    reveiller();
                }
                continue;
            }
            if (ouvertures === 0)
                return;
            await new Promise((resoudre) => endormis.push(resoudre));
        }
    }
    await Promise.all(Array.from({ length: parallelisme }, () => travailleur()));
    for (const etat of etats)
        if (etat.etat === 'attente' || etat.etat === 'envoi')
            echouer(etat, new Error('Envoi interrompu.'));
    return etats.map(suivi);
}
//# sourceMappingURL=depotEnvoi.js.map